import { lazy } from 'preact/compat'
import { QueryClientProvider } from 'react-query'
import {
  useStore,
  UserContext,
  ColorTheme,
  Router,
  ComponentDocs,
  Search,
  queryClient,
} from '@/core'
import { environment } from '@/consts'
import { BootstrapGate } from '@u/drf/BootstrapGate'
import { Header } from '@c/header'
import { ErrorDetailModal } from '@/core/components/form/error'
import { documentedComponents } from '@/components'
import styles from './style.module.less'
import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication } from '@azure/msal-browser'
import { msalConfig } from '../auth-app/microsoft-button/msalConfig'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { I18nextProvider } from 'react-i18next'
import i18n from '@u/i18n/config.js'

const AuthApp = lazy(() => import('@c/auth-app').then(m => ({ default: m.AuthApp })))
const NonprofitAuthApp = lazy(() => import('@c/nonprofit-auth-app').then(m => ({ default: m.NonprofitAuthApp })))
const AdminApp = lazy(() => import('@c/admin-app').then(m => ({ default: m.AdminApp })))
const OnboardingApp = lazy(() => import('@c/onboarding-app').then(m => ({ default: m.OnboardingApp })))
const CompanyApp = lazy(() => import('@c/company-app').then(m => ({ default: m.CompanyApp })))
const NonprofitApp = lazy(() => import('@c/nonprofit-app').then(m => ({ default: m.NonprofitApp })))
const ImpactReportPage = lazy(() => import('@c/impact-report').then(m => ({ default: m.ImpactReportPage })))
const TeamPageApp = lazy(() => import('@c/team-page-app').then(m => ({ default: m.TeamPageApp })))
const LandingPageApp = lazy(() => import('@c/landing-page-app').then(m => ({ default: m.LandingPageApp })))
const NonprofitLandingPageApp = lazy(() => import('@c/landing-page-app/nonprofit-landing-page').then(m => ({ default: m.NonprofitLandingPageApp })))
const AboutPageApp = lazy(() => import('@c/about-app').then(m => ({ default: m.AboutPageApp })))
const PrivacyPolicy = lazy(() => import('@c/legal-info-app/privacy-policy').then(m => ({ default: m.PrivacyPolicy })))
const TermsOfUse = lazy(() => import('@c/legal-info-app/terms-of-use').then(m => ({ default: m.TermsOfUse })))
const Subprocessors = lazy(() => import('@c/legal-info-app/subprocessors').then(m => ({ default: m.Subprocessors })))
const Trust = lazy(() => import('@c/legal-info-app/trust').then(m => ({ default: m.Trust })))
const ReferralPage = lazy(() => import('@c/landing-page-app/referral-page').then(m => ({ default: m.ReferralPage })))
const ResourcesApp = lazy(() => import('@c/resources-app').then(m => ({ default: m.ResourcesApp })))
const OpportunityPage = lazy(() => import('@c/discover/opportunity-page').then(m => ({ default: m.OpportunityPage })))
const EventSignInPage = lazy(() => import('@c/auth-app/event-sign-in-page').then(m => ({ default: m.EventSignInPage })))

import { authRouteNames } from '@c/auth-app/route-names'
import { nonprofitAuthRouteNames } from '@c/nonprofit-auth-app/route-names'
import { adminRouteNames } from '@c/admin-app/route-names'
import { onboardingRouteNames } from '@c/onboarding-app/route-names'
import { companyRouteNames } from '@c/company-app/route-names'
import { nonprofitRouteNames } from '@c/nonprofit-app/route-names'
import { resourcesRouteNames } from '@c/resources-app/route-names'

const pca = new PublicClientApplication(msalConfig)

const BaseUserProvider = ({ children }) => {
  const store = useStore()
  const [user] = store.use(['user'])
  return <UserContext.Provider value={user}>{children}</UserContext.Provider>
}

const logoutCheck = () => {
  const token = localStorage?.getItem('token')
  const users = localStorage?.getItem('users')
  if (token && !users) {
    window.localStorage.clear()
  }
}

logoutCheck()

const GOOGLE_ID =
  environment === 'production'
    ? '1073120797777-7gvrptut97on6t7vrt2gjfp7bruk3tbi.apps.googleusercontent.com'
    : '1079004936560-9llo4vr8fig46g0h7l2t2lg68t3ob83v.apps.googleusercontent.com'

export const App = (props) => {
  return (
    <ColorTheme root>
      <MsalProvider instance={pca}>
        <GoogleOAuthProvider clientId={GOOGLE_ID}>
          <I18nextProvider i18n={i18n}>
            <QueryClientProvider client={queryClient}>
              <BaseUserProvider>
                <div className={styles.app}>
                  <ErrorDetailModal />
                  <Search />
                  <BootstrapGate>
                    <Router
                      routes={[
                        ['frontend2', () => 'Hello from frontend2'],
                        ['todo', () => 'Todo'],
                        [authRouteNames, AuthApp],
                        [nonprofitAuthRouteNames, NonprofitAuthApp],
                        [adminRouteNames, AdminApp],
                        [onboardingRouteNames, OnboardingApp],
                        ['impactReport', ImpactReportPage],
                        [companyRouteNames, CompanyApp],
                        [nonprofitRouteNames, NonprofitApp],
                        ['teamPage', TeamPageApp],
                        ['landingPage', LandingPageApp],
                        ['nonprofitLandingPage', NonprofitLandingPageApp],
                        ['aboutUsPage', AboutPageApp],
                        ['privacyPolicy', PrivacyPolicy],
                        ['termsOfUse', TermsOfUse],
                        ['subprocessors', Subprocessors],
                        ['trust', Trust],
                        [resourcesRouteNames, ResourcesApp],
                        ['opportunityPage', OpportunityPage],
                        ['eventSignIn', EventSignInPage],
                        ['referralProgram', ReferralPage],

                        [
                          'componentDocs',
                          () => (
                            <div>
                              <Header />
                              <ComponentDocs
                                components={documentedComponents}
                              />
                            </div>
                          ),
                        ],
                        [
                          'componentDocsRender',
                          () => <div id="component-render" />,
                        ],
                      ]}
                    />
                  </BootstrapGate>
                </div>
              </BaseUserProvider>
            </QueryClientProvider>
          </I18nextProvider>
        </GoogleOAuthProvider>
      </MsalProvider>
    </ColorTheme>
  )
}
